<template>
  <div
    class="step-holder"
    v-bind:class="{ completed: activeJobEditStep > 3, active: activeJobEditStep === 3 }"
  >
    <div class="title-bar" style="display: flex; justify-content: space-between">
      <h3><span :class="{ invalid: isPdInvalid }">3. Pupil Distance</span></h3>
      <div>
        <a class="ml-4" @click="editThisStep" v-if="activeJobEditStep > 3 && this.customer"
          >Change</a
        >
        <a class="ml-4" @click="resetThisStep" v-if="activeJobEditStep > 3 && this.customer"
          >Reset</a
        >

        <a @click="editPd()" v-if="!customer && selectedPd">Guest Edit</a>

        <a @click="createPd()" v-if="!customer && !selectedPd">Create Pd</a>
      </div>
    </div>

    <v-wait for="Pd List">
      <template slot="waiting">
        <div class="text-center mb-3">
          <b-spinner></b-spinner>
        </div>
      </template>
      <div style="width: 100%">
        <template v-if="activeJobEditStep > 3 && selectedPd">
          <div class="pd-detail-box" style="width: 100%">
            <h6 class="text-uppercase">{{ isMultifocal ? 'Width' : '' }}</h6>
            <p>
              <PupilDistanceDetail
                :pd="presenter"
                type="width"
                :sourceMessage="sourceMessage"
              ></PupilDistanceDetail>
            </p>
          </div>
          <div class="pd-detail-box ml-3" v-if="hasPdHeight">
            <h6 class="text-uppercase">{{ isMultifocal ? 'Height' : '' }}</h6>
            <p>
              <PupilDistanceDetail
                :pd="presenter"
                type="height"
                :sourceMessage="sourceMessage"
              ></PupilDistanceDetail>
            </p>
          </div>
          <div v-if="presenterNote">
            <p class="warning mr-2 mt-3">{{ presenterNote }}</p>
          </div>
        </template>

        <ValidationObserver v-slot="{ handleSubmit, invalid }">
          <b-form @submit.stop.prevent="handleSubmit(sendPdForm)" v-if="activeJobEditStep === 3">
            <ValidationProvider name="Pupil Distance" rules="required" v-slot="validationContext">
              <PupilDistanceList
                v-model="$store.state.jobsModule.selectedPd"
                :customer="{ ...customer, ...customerPds }"
                :showTitle="true"
                :selectable="true"
                :expanded="true"
                :collapsible="true"
                @new="showPdModal"
                @edit="showPdEditModal"
                @copy="showPdCopyModal"
              ></PupilDistanceList>
              <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
            </ValidationProvider>

            <div class="confirmation-wrapper">
              <b-button class="mr-3" variant="outline-secondary" @click="cancel" type="button"
                >Cancel</b-button
              >
              <b-button
                class="button-with-loader"
                type="submit"
                :disabled="invalid || isPdInvalid"
                variant="primary"
                >Confirm</b-button
              >
            </div>
          </b-form>
        </ValidationObserver>
      </div>
    </v-wait>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import PupilDistanceList from '../../PupilDistanceList.vue';
import PupilDistanceDetail from '../../PupilDistanceDetail.vue';

export default {
  name: 'PupilDistance',
  props: {
    lenses: Object,
    additionalInfo: Object,
    customerPds: Object,
    customer: Object,
    item: Object,
  },
  computed: {
    ...mapState({
      activeJobEditStep: state => state.jobsModule.activeJobEditStep,
      selectedPd: state => state.jobsModule.selectedPd,
    }),
    isPdInvalid() {
      return !!this.selectedPd?.error || !!this.selectedPd?.pdPresenter?.error;
    },
    sourceMessage() {
      const sourceId = this._.get(this.selectedPd, 'detail.source_id');
      if (sourceId && this.$store.state.pdSources) {
        const selectedSource = this.$store.state.pdSources.find(
          source => source.slug === 'o-pm' && source.id === sourceId,
        );
        if (selectedSource) {
          return true;
        }
      }
      return false;
    },

    pdType() {
      if (this.selectedPd && this.selectedPd.pdPresenter && this.selectedPd.pdPresenter.height) {
        return 'height';
      }
      return 'width';
    },
    isMultifocal() {
      return this._.get(this.item, 'region_product.product.type.lenses_type') === 'multifocal';
    },
    // api-v4 builds the presenter from the item's lens type; with no lens chosen yet (e.g. a
    // prescription no lens covers) its data is empty, so the PD is shown as saved
    presenter() {
      if (this.selectedPd?.pdPresenter?.data) return this.selectedPd.pdPresenter;
      const pd = this.selectedPd?.detail;
      if (!pd) return null;
      const widths = zone => ({
        binocular: pd[`binocular_${zone}_pd`],
        right: pd[`right_monocular_${zone}_pd`],
        left: pd[`left_monocular_${zone}_pd`],
      });
      const zone =
        ['dist', 'int', 'reading'].find(z => Object.values(widths(z)).some(v => v)) || 'dist';
      const heightType = ['multifocal', 'bifocal', 'desk', 'aspheric'].find(
        t => pd[`right_height_${t}`] || pd[`left_height_${t}`],
      );
      return {
        ...this.selectedPd.pdPresenter,
        data: {
          width: { ...widths(zone), pdZone: zone },
          height: heightType
            ? {
                right: pd[`right_height_${heightType}`],
                left: pd[`left_height_${heightType}`],
                pdZone: 'bottom',
              }
            : null,
        },
      };
    },
    hasPdHeight() {
      return this.presenter?.data?.height?.right;
    },
    presenterNote() {
      return this._.get(this.selectedPd?.detail, 'note');
    },
  },
  
  watch: {
    customerPds: {
      handler(newCustomerPds) {
        // Force re-render when customerPds changes
        this.$forceUpdate();
      },
      deep: true
    }
  },
  components: {
    PupilDistanceList,
    PupilDistanceDetail,
    ValidationProvider,
    ValidationObserver,
  },
  
  mounted() {
    // Listen for PD updates to refresh the component
    this.$root.$on('newPdAdded', this.handlePdUpdate);
  },
  
  beforeDestroy() {
    this.$root.$off('newPdAdded', this.handlePdUpdate);
  },
  methods: {
    editPd() {
      this.$emit('show-pd-edit-modal', {
        pd: this.selectedPd.detail,
        isOnPageEdit: true,
      });
    },
    createPd() {
      this.$emit('show-pd-modal', {
        isOnPageEdit: false,
      });
    },
    showPdModal(e) {
      this.$emit('show-pd-modal', e);
    },
    editThisStep() {
      this.$store.commit('jobsModule/SET_ACTIVE_JOB_EDIT_STEP', 3);
    },
    sendPdForm() {
      this.$emit('pdDataSent', this.selectedPd);
      this.$store.commit('jobsModule/SET_ACTIVE_JOB_EDIT_STEP', 4);
    },
    cancel() {
      this.$emit('pdDataSent', this.selectedPd);
      this.$store.commit('jobsModule/SET_ACTIVE_JOB_EDIT_STEP', 4);
    },
    resetThisStep() {
      this.$store.commit('jobsModule/SET_ACTIVE_JOB_EDIT_STEP', 3);
      this.$store.commit('jobsModule/SET_SELECTED_PD', null);
    },
    showPdEditModal(e) {
      this.$emit('show-pd-edit-modal', e);
    },
    showPdCopyModal(e) {
      this.$emit('show-pd-copy-modal', e);
    },
    
    handlePdUpdate(pdData = null) {
      // Force a re-render of the component when PD data is updated
      this.$forceUpdate();
    },
  },
};
</script>
